import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Category, CategoryPluRange, CategoryPluRangeParams, CategorySummary } from '../models/response-models/category';
import { environment } from '../../environments/environment';
import { UntypedFormGroup } from '@angular/forms';
import { ProductCategoryDisplay } from '../models/response-models/product-category-display';
import { ModifierGroup } from '../models/response-models/modifier-group';
import { RoutingTag } from '../models/response-models/routing-tag';
import { OrderType } from '../models/response-models/order-type';
import { Course } from '../models/response-models/course';
import { RelationshipListElement } from '../models/application-models/relationship-list-element';

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  previousCategoryFilter: Category[] = [];
  fullSelectedCategoryList: Category[] = [];

  constructor(
    private http: HttpClient
  ) { }

  getAllCategories(): Observable<Category[]>{
    return this.http.get<Category[]>(environment.serverUrl + "/productCategories");
  }

  getSiteProductCategoriesForUser(userID: string): Observable<Category[]>{
    return this.http.get<Category[]>(environment.serverUrl + `/productCategories/user/${userID}`);
  }

  getRootCategories(): Observable<Category[]>{
    return this.http.get<Category[]>(environment.serverUrl + "/productCategories/rootCategories");
  }

  getSubCategoriesByCategory(parentCategory: Category): Observable<Category[]>{
    let categoryID: string;

    if(!parentCategory) categoryID = "00000000-0000-0000-0000-000000000000";
    else categoryID = parentCategory.id;

    return this.http.get<Category[]>(environment.serverUrl + `/productCategories/${categoryID}/categoryDescendants`);
  }

  getCategoriesSummaryByCategory(parentCategory: Category): Observable<CategorySummary[]>{
    let categoryID: string;

    if(!parentCategory) categoryID = "00000000-0000-0000-0000-000000000000";
    else categoryID = parentCategory.id;

    return this.http.get<CategorySummary[]>(environment.serverUrl + `/productCategories/${categoryID}/categoriesSummary`);
  }

  getCategorySummariesByUser(userID: string): Observable<CategorySummary[]>{
    return this.http.get<CategorySummary[]>(environment.serverUrl + `/productCategories/${userID}/categorySummaries`);
  }

  getCategoryByID(categoryID: string): Observable<Category>{
    return this.http.get<Category>(environment.serverUrl + `/productCategories/${categoryID}`);
  }

  getModifierGroups(): Observable<ModifierGroup[]>{
    return this.http.get<ModifierGroup[]>(environment.serverUrl + `/modifierGroups`);
  }

  getRoutingTags(): Observable<RoutingTag[]>{
    return this.http.get<RoutingTag[]>(environment.serverUrl + `/routing/tags`);
  }

  getOrderTypes(): Observable<OrderType[]>{
    return this.http.get<OrderType[]>(environment.serverUrl + `/references/orderTypes`);
  }

  createProductCategoryDisplay(category: Category){
    let id = category.id;

    let options = category.inheritedDisplay;

    if(options.borderRadius.indexOf("px") > -1){
      options.borderRadius = options.borderRadius.substring(0, options.borderRadius.length -2);
    } else {
      options.borderRadius = options.borderRadius.substring(0, options.borderRadius.length);
    }
    options.id = id;

    return this.http.post<ProductCategoryDisplay>(environment.serverUrl + `/productCategories/${id}/productCategoryDisplay`, options);
  }

  editProductCategoryDisplay(form: UntypedFormGroup): Observable<ProductCategoryDisplay>{
    let id = form.controls.id.value;

    let options = {
      borderColour: form.controls.borderColor.value,
      borderRadius: form.controls.borderRadius.value,
      borderStyle: form.controls.borderStyle.value,
      borderWeight: form.controls.borderWeight.value,
      fillCSS: form.controls.backgroundColor.value,
      fillType: form.controls.buttonFill.value,
      font: form.controls.fontType.value,
      fontColour: form.controls.fontColor.value,
      fontIsBold: form.controls.isBold.value,
      fontIsItalic: form.controls.isItalic.value,
      fontIsUnderline: form.controls.isUnderlined.value,
      fontSize: form.controls.fontSize.value + "pt",
      id: id
    };

    return this.http.put<ProductCategoryDisplay>(environment.serverUrl + `/productCategories/${id}/productCategoryDisplay`, options);
  }

  createProductCategory(form: UntypedFormGroup): Observable<Category>{
    let options = {
      id: form.controls.id.value,
      name: form.controls.categoryName.value,
      sellOnPOS: form.controls.sellOnPos.value,
      vatRateID: form.controls.categoryVatRate ? form.controls.categoryVatRate.value?.id : null,
      targetMargin: form.controls.targetMargin.value,
      courseID: form.controls.course.value.id,
      isCourseInherited: form.controls.isCourseInherited.value,
      isActive: form.controls.isActive.value,
      parentID: form.controls.parentCategory.value ? form.controls.parentCategory.value : "00000000-0000-0000-0000-000000000000",
      index: 0,
      dashboardCardColour: form.controls.cardColour.value,
      dashboardIcon: form.controls.displayIcon.value?.prefix + ' ' + form.controls.displayIcon.value?.definition,
      stagingAreaID: form.controls.stagingAreaID?.value
    };

    return this.http.post<Category>(environment.serverUrl + `/productCategories`, options);
  }

  editProductCategory(form: UntypedFormGroup): Observable<Category>{
    let options = {
      id: form.controls.id.value,
      name: form.controls.categoryName.value,
      sellOnPOS: form.controls.sellOnPos.value,
      vatRateID: form.controls.categoryVatRate ? form.controls.categoryVatRate.value ? form.controls.categoryVatRate.value.id : null : null,
      targetMargin: form.controls.targetMargin.value,
      courseID: form.controls.course.value.id,
      isCourseInherited: form.controls.isCourseInherited.value,
      isActive: form.controls.isActive.value,
      parentID: form.controls.parentCategory.value ? form.controls.parentCategory.value : "00000000-0000-0000-0000-000000000000",
      index: 0,
      dashboardCardColour: form.controls.cardColour.value,
      dashboardIcon: form.controls.displayIcon.value?.prefix + ' ' + form.controls.displayIcon.value?.definition,
      stagingAreaID: form.controls.stagingAreaID?.value
    };

    return this.http.put<Category>(environment.serverUrl + `/productCategories`, options);
  }

  getCourses(): Observable<Course[]>{
    return this.http.get<Course[]>(environment.serverUrl + `/references/courses`);
  }

  manageModifierGroupByID(categoryID: string, relationshipList: RelationshipListElement[]){
    let options = relationshipList;

    return this.http.post<any>(environment.serverUrl + `/productCategories/${categoryID}/modifierGroups`, options);
  }

  manageRoutingTagByID(categoryID: string, relationshipList: RelationshipListElement[]){
    let options = relationshipList;

    return this.http.post<any>(environment.serverUrl + `/productCategories/${categoryID}/routeTags`, options);
  }

  manageOrderTypeByID(categoryID: string, relationshipList: RelationshipListElement[]){
    let options = relationshipList;

    return this.http.post<any>(environment.serverUrl + `/productCategories/${categoryID}/orderTypes`, options);
  }

  manageSiteByID(categoryID: string, relationshipList: RelationshipListElement[]){
    let options = relationshipList;

    return this.http.post<any>(environment.serverUrl + `/productCategories/${categoryID}/sites`, options);
  }

  removeAllSites(categoryID: string){
    return this.http.delete<any>(environment.serverUrl + `/productCategories/${categoryID}/sites`);
  }

  manageTaxGroupByID(categoryID: string, relationshipList: RelationshipListElement[]){
    let options = relationshipList;

    return this.http.post<any>(environment.serverUrl + `/productCategories/${categoryID}/taxGroups`, options);
  }

  removeCategoryByID(categoryID: string){
    let options: any = {
      body: {
        id: categoryID
      }
    };

    return this.http.delete<any>(environment.serverUrl + `/productCategories`, options);
  }

  patchProductCategoryDisplay(categoryID: string){
    return this.http.patch<any>(environment.serverUrl + `/productCategories/${categoryID}/productCategoryDisplay`, {});
  }

  patchModifierGroups(categoryID: string){
    return this.http.patch<any>(environment.serverUrl + `/productCategories/${categoryID}/modifierGroups`, {});
  }

  patchVatRates(categoryID: string){
    return this.http.patch<any>(environment.serverUrl + `/productCategories/${categoryID}/vatRates`, {});
  }

  patchRoutingTags(categoryID: string){
    return this.http.patch<any>(environment.serverUrl + `/productCategories/${categoryID}/routeTags`, {});
  }

  createListFromCategoryWhichHasNoChildren(categories: Category[]): Category[]{
    let categoryWithoutChildren: Category[] = [];
    for(let category of categories){
      if(!category.children.length){
        categoryWithoutChildren.push(category)
      }
    }

    return categoryWithoutChildren;
  }

  clearSearchFilters(){
    this.previousCategoryFilter = [];
    this.fullSelectedCategoryList = [];
  }

  addScheduleRelationship(categoryID: string, scheduleItem: RelationshipListElement[]){
    let options = scheduleItem

    return this.http.post<OrderType>(environment.serverUrl + `/references/categories/${categoryID}/schedules`, options);
  }

  getPluRangeGroups(){
    return this.http.get<CategoryPluRange[]>(environment.serverUrl + `/productCategories/pluGroups`);
  }

  addPluGroup(params: CategoryPluRangeParams){
    return this.http.post<CategoryPluRange>(environment.serverUrl + '/productCategories/pluGroup', params);
  }

  updatePluGroup(params: CategoryPluRangeParams){
    return this.http.put<CategoryPluRange>(environment.serverUrl + '/productCategories/pluGroup', params);
  }

  deletePluGroup(pluGroupId: string){
    return this.http.delete<CategoryPluRange>(environment.serverUrl + `/productCategories/pluGroup/${pluGroupId}`);
  }

  getNextPluByCategory(categoryId: string){
    return this.http.get<number>(environment.serverUrl + `/productCategories/nextPLU/${categoryId}`);
  }

}
